import { Project, Task } from '../types/Project';
import { EVMData, EVMMetrics, ProjectEVM } from '../types/EVM';
import { differenceInDays, addDays, isBefore, isAfter } from 'date-fns';

// プロジェクトからEVMデータを計算
export const calculateProjectEVM = (
  project: Project,
  tasks: Task[],
  currentDate: Date = new Date()
): ProjectEVM => {
  const projectTasks = tasks.filter(task => task.projectId === project.id);
  const totalBudget = project.progress * 1000; // 仮の予算計算（進捗 × 1000万円）
  
  // EVMデータの時系列を生成
  const evmData = generateEVMTimeSeries(project, projectTasks, totalBudget, currentDate);
  
  // 現在のメトリクスを計算
  const metrics = calculateCurrentMetrics(project, projectTasks, totalBudget, currentDate);
  
  return {
    projectId: project.id,
    projectName: project.name,
    startDate: project.startDate,
    endDate: project.endDate,
    totalBudget,
    currentDate,
    evmData,
    metrics
  };
};

// EVM時系列データの生成
const generateEVMTimeSeries = (
  project: Project,
  tasks: Task[],
  totalBudget: number,
  currentDate: Date
): EVMData[] => {
  const data: EVMData[] = [];
  const totalDays = differenceInDays(project.endDate, project.startDate);
  
  // プロジェクト開始日から現在日まで、または終了日まで
  const endCalcDate = isBefore(currentDate, project.endDate) ? currentDate : project.endDate;
  const calcDays = differenceInDays(endCalcDate, project.startDate);
  
  for (let i = 0; i <= calcDays; i++) {
    const date = addDays(project.startDate, i);
    
    // PV: 計画価値（線形計画）
    const plannedValue = (i / totalDays) * totalBudget;
    
    // EV: 出来高価値（タスクの完了状況に基づく）
    const earnedValue = calculateEarnedValue(tasks, date, totalBudget);
    
    // AC: 実績コスト（出来高 × 1.1〜0.9の範囲でランダム）
    const actualCost = earnedValue * (0.9 + Math.random() * 0.2);
    
    data.push({
      date,
      plannedValue,
      earnedValue,
      actualCost
    });
  }
  
  return data;
};

// 出来高価値の計算
const calculateEarnedValue = (tasks: Task[], date: Date, totalBudget: number): number => {
  let completedValue = 0;
  const totalTasks = tasks.length;
  
  if (totalTasks === 0) return 0;
  
  const taskBudget = totalBudget / totalTasks;
  
  tasks.forEach(task => {
    if (isBefore(date, task.startDate)) {
      // タスク開始前：0%
      completedValue += 0;
    } else if (isAfter(date, task.endDate)) {
      // タスク終了後：100%
      completedValue += taskBudget;
    } else {
      // タスク実行中：進捗率に応じて
      const taskProgress = task.progress / 100;
      completedValue += taskBudget * taskProgress;
    }
  });
  
  return completedValue;
};

// 現在のEVMメトリクス計算
const calculateCurrentMetrics = (
  project: Project,
  tasks: Task[],
  totalBudget: number,
  currentDate: Date
): EVMMetrics => {
  const currentDays = differenceInDays(currentDate, project.startDate);
  const totalDays = differenceInDays(project.endDate, project.startDate);
  
  // 基本値 - より実際の値のように変動
  const pv = Math.max(0, Math.min(totalBudget, (currentDays / totalDays) * totalBudget));
  const ev = calculateEarnedValue(tasks, currentDate, totalBudget);
  
  // ACをより実際的な値に調整（タスクの進捗と複雑さに基づく）
  const projectComplexity = Math.max(0.8, Math.min(1.2, 1 + (project.progress - 50) / 100));
  const taskEfficiency = tasks.length > 0 ? 
    tasks.reduce((sum, task) => sum + (task.progress / 100), 0) / tasks.length : 0.5;
  const ac = ev * (0.85 + (taskEfficiency * 0.3) + (Math.random() * 0.2) * projectComplexity);
  
  // 差異指標 - AC、CVを四捨五入
  const cv = Math.round(ev - ac); // コスト差異
  const sv = ev - pv; // スケジュール差異
  
  // パフォーマンス指標
  const cpi = ac > 0 ? ev / ac : 1; // コスト効率指数
  const spi = pv > 0 ? ev / pv : 1; // スケジュール効率指数
  
  // 予測指標
  const bac = totalBudget; // 完成時予算
  const etc = cpi > 0 ? (bac - ev) / cpi : bac - ev; // 完成までの予測コスト
  const eac = ac + etc; // 完成時コスト予測
  const tcpi = (bac - ev) > 0 ? (bac - ev) / (bac - ac) : 1; // 完成までに必要な効率指数
  
  // 進捗率
  const progressPercentage = (ev / bac) * 100;
  
  return {
    pv,
    ev,
    ac,
    cv,
    sv,
    cpi,
    spi,
    bac,
    etc,
    eac,
    tcpi,
    progressPercentage
  };
};

// メトリクスの解釈テキスト
export const getMetricInterpretation = (metrics: EVMMetrics) => {
  const interpretations = {
    costStatus: metrics.cv >= 0 ? 'コスト予算内' : 'コスト超過',
    scheduleStatus: metrics.sv >= 0 ? 'スケジュール順調' : 'スケジュール遅延',
    costEfficiency: metrics.cpi >= 1 ? '効率良好' : '効率悪化',
    scheduleEfficiency: metrics.spi >= 1 ? 'ペース順調' : 'ペース遅延',
    overallHealth: getOverallHealth(metrics)
  };
  
  return interpretations;
};

const getOverallHealth = (metrics: EVMMetrics): string => {
  if (metrics.cpi >= 1 && metrics.spi >= 1) return '良好';
  if (metrics.cpi >= 0.9 && metrics.spi >= 0.9) return '注意';
  return '要改善';
};

// 通貨フォーマット
export const formatCurrency = (amount: number, currency: string = '¥'): string => {
  return `${currency}${amount.toLocaleString('ja-JP')}`;
};

// 四捨五入通貨フォーマット（AC、CV用）
export const formatCurrencyRounded = (amount: number, currency: string = '¥'): string => {
  return `${currency}${Math.round(amount).toLocaleString('ja-JP')}`;
};

// パーセンテージフォーマット
export const formatPercentage = (value: number): string => {
  return `${(value * 100).toFixed(1)}%`;
};
